<template>
  <div class="attendance-tab">
    <div class="attendance-tab__intro">
      <div class="text-h6 font-weight-bold">
        {{ $t("attendanceTab.title") }}
      </div>
      <div class="attendance-tab__hint">
        {{ $t("attendanceTab.hint") }}
      </div>
    </div>

    <page-skeleton v-if="loading" variant="rows" :rows="4" class="mt-4" />

    <v-alert v-else-if="!members.length" type="info" text class="mt-4 mb-0">
      {{ $t("attendanceTab.noMembers") }}
    </v-alert>

    <v-alert
      v-else-if="!availableMonths.length"
      type="info"
      text
      class="mt-4 mb-0"
    >
      {{ $t("attendanceTab.noLessons") }}
    </v-alert>

    <template v-else>
      <v-row dense class="mt-3">
        <v-col cols="12" md="5">
          <v-select
            v-model="selectedMonthKey"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            :label="$t('attendance.referenceMonth')"
            outlined
            dense
            hide-details="auto"
            :disabled="loading"
            @change="handleMonthChange"
          />
        </v-col>

        <v-col cols="12" md="7">
          <v-select
            v-model="selectedLessonId"
            :items="lessonOptions"
            item-text="text"
            item-value="value"
            :label="$t('attendanceTab.lesson')"
            outlined
            dense
            hide-details="auto"
            :disabled="loading || !lessons.length"
            @change="handleLessonChange"
          />
        </v-col>
      </v-row>

      <v-alert v-if="!lessons.length" type="info" text class="mt-4 mb-0">
        {{ $t("attendance.noLessonsMonth") }}
      </v-alert>

      <template v-else-if="selectedLesson">
        <div class="attendance-tab__lesson-head mt-5">
          <div>
            <div class="attendance-tab__lesson-title">
              {{ selectedLesson.title || $t("attendanceTab.lesson") }}
            </div>
            <div class="attendance-tab__lesson-date">
              {{ formatLessonDate(selectedLesson.eventDate) }} ·
              {{ selectedLesson.startsAt }}
            </div>
          </div>

          <v-chip v-if="selectedLesson.isFuture" small outlined color="warning">
            <v-icon x-small left>mdi-clock-outline</v-icon>
            {{ $t("attendanceTab.futureLesson") }}
          </v-chip>

          <div v-else class="attendance-tab__quick">
            <v-btn x-small text rounded color="#00A379" @click="markAll(1)">
              {{ $t("attendanceTab.allPresent") }}
            </v-btn>
            <v-btn x-small text rounded color="#D52323" @click="markAll(2)">
              {{ $t("attendanceTab.allAbsent") }}
            </v-btn>
          </div>
        </div>

        <v-row dense class="mt-3">
          <!-- Nivel e assunto sao dois passos de proposito. Antes a lista era
               montada so com o nivel da turma (mais conversacao), entao a aula
               efetivamente dada num outro nivel nao existia como opcao e o
               professor nao conseguia registrar a presenca dela. -->
          <v-col cols="12" md="4">
            <v-select
              v-model="subjectLevel"
              :items="levelOptions"
              item-text="text"
              item-value="value"
              :label="$t('attendanceTab.levelTaught')"
              prepend-inner-icon="mdi-stairs"
              outlined
              dense
              hide-details="auto"
              :loading="loadingSubjects"
              :disabled="savingSubject"
              @change="changeSubjectLevel"
            />
          </v-col>

          <v-col cols="12" md="8">
            <v-select
              v-model="lessonSubjectId"
              :items="subjectOptions"
              item-text="title"
              item-value="id"
              :label="$t('attendanceTab.subject')"
              :hint="$t('attendanceTab.subjectHint')"
              persistent-hint
              prepend-inner-icon="mdi-book-open-variant"
              outlined
              dense
              :loading="loadingSubjects || savingSubject"
              :disabled="!subjectOptions.length || savingSubject"
              @change="changeSubject"
            >
              <template #item="{ item }">
                <div class="attendance-subject">
                  <span class="attendance-subject__index">{{
                    item.position
                  }}</span>
                  <span class="attendance-subject__title">{{
                    item.title
                  }}</span>
                  <LessonTagChips
                    :category="item.category"
                    :pre="item.pre"
                    :optional="item.optional"
                  />
                </div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-alert
          v-if="!loadingSubjects && !subjectOptions.length"
          type="warning"
          text
          dense
          class="mt-2 mb-0"
        >
          {{ $t("attendanceTab.noContent") }}
        </v-alert>

        <div
          v-if="selectedLesson.courseClassId && !selectedLesson.isFuture"
          class="attendance-tab__course-hint mt-3"
        >
          <v-icon x-small color="#00A379" class="mr-1">
            mdi-check-decagram-outline
          </v-icon>
          {{ $t("attendanceTab.courseHint") }}
        </div>

        <v-alert v-if="lessonLevelMismatch" type="info" text class="mt-3 mb-0">
          {{ $t("attendanceTab.levelMismatch") }}
        </v-alert>

        <v-alert
          v-if="!rosterMembers.length"
          type="info"
          text
          class="mt-4 mb-0"
        >
          {{ $t("attendanceTab.noActive") }}
        </v-alert>

        <div v-else class="attendance-tab__roster mt-4">
          <div
            v-for="student in rosterMembers"
            :key="student.id"
            class="attendance-row"
          >
            <div class="d-flex align-center">
              <v-avatar
                size="40"
                class="mr-3"
                :color="student.avatar ? '' : theme.primary"
              >
                <v-img v-if="student.avatar" :src="student.avatar" cover />
                <span v-else class="white--text attendance-row__initials">
                  {{ initials(student.name) }}
                </span>
              </v-avatar>
              <div>
                <div class="attendance-row__name">
                  {{ student.name || $t("common.student") }}
                  <v-tooltip bottom v-if="!isLinkedToEvent(student.id)">
                    <template #activator="{ on, attrs }">
                      <v-icon
                        x-small
                        color="#94a3b8"
                        class="ml-1"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>
                      {{ $t("attendanceTab.notInOriginal") }}
                    </span>
                  </v-tooltip>
                </div>
                <div
                  v-if="selectedLesson.courseClassId"
                  class="attendance-row__subject"
                  :class="{
                    'attendance-row__subject--done':
                      savedStatusOf(student.id) === 1,
                  }"
                >
                  <v-icon x-small class="mr-1">
                    {{
                      savedStatusOf(student.id) === 1
                        ? "mdi-check-circle"
                        : "mdi-circle-outline"
                    }}
                  </v-icon>
                  {{
                    savedStatusOf(student.id) === 1
                      ? $t("attendanceTab.subjectDone")
                      : $t("attendanceTab.subjectNotDone")
                  }}
                </div>
              </div>
            </div>

            <div class="attendance-row__toggle">
              <v-btn
                small
                rounded
                class="attendance-row__btn"
                :depressed="statusOf(student.id) === 1"
                :outlined="statusOf(student.id) !== 1"
                :color="statusOf(student.id) === 1 ? '#00A379' : '#94a3b8'"
                :dark="statusOf(student.id) === 1"
                :disabled="selectedLesson.isFuture"
                @click="setStatus(student.id, 1)"
              >
                <v-icon x-small left>mdi-check</v-icon>
                {{ $t("common.present") }}
              </v-btn>

              <v-btn
                small
                rounded
                class="attendance-row__btn ml-2"
                :depressed="statusOf(student.id) === 2"
                :outlined="statusOf(student.id) !== 2"
                :color="statusOf(student.id) === 2 ? '#D52323' : '#94a3b8'"
                :dark="statusOf(student.id) === 2"
                :disabled="selectedLesson.isFuture"
                @click="setStatus(student.id, 2)"
              >
                <v-icon x-small left>mdi-close</v-icon>
                {{ $t("common.absent") }}
              </v-btn>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap justify-end align-center mt-6">
          <div v-if="isDirty" class="caption mr-4 attendance-tab__unsaved">
            {{ $t("attendanceTab.unsaved") }}
          </div>

          <v-btn
            rounded
            :color="theme.primary"
            class="white--text"
            :loading="saving"
            :disabled="!isDirty || selectedLesson.isFuture"
            @click="save"
          >
            {{ $t("attendanceTab.save") }}
          </v-btn>
        </div>

        <ClassLessonFollowUp
          :group-id="groupId"
          :event-id="String(selectedLesson.id)"
          :course-class-id="selectedLesson.courseClassId || null"
          :subject-title="subjectTitle"
          :members="members"
          :students="rosterMembers"
          :theme="theme"
        />
      </template>
    </template>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { SUBJECT_LEVELS, initialsFromName } from "@/shared/classPresentation";
import LessonTagChips from "@/components/connect/course/LessonTagChips.vue";
import ClassLessonFollowUp from "@/components/classes/ClassLessonFollowUp.vue";
import { lessonPosition } from "@/shared/courseTiers";
import { formatMonthYear, languageTag } from "@/plugins/i18n";

export default {
  name: "ClassAttendanceTab",

  components: { LessonTagChips, ClassLessonFollowUp },

  props: {
    groupId: {
      type: [String, Number],
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
    groupLevel: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      availableMonths: [],
      selectedMonthKey: null,
      lessons: [],
      selectedLessonId: null,
      draftsByLesson: {},
      snapshotsByLesson: {},
      subjectLevel: null,
      // Cache por nivel: trocar de nivel nao pode custar uma ida ao servidor
      // toda vez, e o professor costuma alternar entre dois ou tres.
      subjectsByLevel: {},
      // id do assunto -> nivel dele. Guardado a parte porque o assunto ja
      // gravado na aula pode ser de um nivel que ainda nao foi carregado, e o
      // aviso de divergencia precisa saber disso.
      subjectLevelById: {},
      loadingSubjects: false,
      savingSubject: false,
      lessonSubjectId: null,
    };
  },

  computed: {
    monthOptions() {
      return this.availableMonths.map((item) => ({
        text: formatMonthYear(item.month, item.year) || item.label,
        value: item.key,
      }));
    },
    lessonOptions() {
      return this.lessons.map((lesson) => ({
        text: `${this.formatLessonDate(lesson.eventDate)} · ${lesson.startsAt}${
          lesson.isFuture ? this.$t("attendanceTab.futureSuffix") : ""
        }`,
        value: lesson.id,
      }));
    },
    selectedLesson() {
      return (
        this.lessons.find((lesson) => lesson.id === this.selectedLessonId) ||
        null
      );
    },
    // Todos os alunos ativos da turma entram na chamada. Filtrar por
    // `participants` escondia quem entrou na turma depois de a aula ser gerada
    // ou quem estava num nível diferente do tema — e o professor não conseguia
    // marcar a presença de quem tinha assistido.
    rosterMembers() {
      if (!this.selectedLesson) {
        return [];
      }

      const roster = this.selectedLesson.roster || [];

      if (!roster.length) {
        return this.members;
      }

      const order = new Map(
        roster.map((entry, index) => [entry.customerId, index])
      );

      return this.members
        .filter((member) => order.has(member.id))
        .sort((left, right) => order.get(left.id) - order.get(right.id));
    },
    levelOptions() {
      return SUBJECT_LEVELS.map((level) => ({
        value: level.value,
        text:
          +level.value === +this.groupLevel
            ? this.$t("attendanceTab.classLevel", { level: level.text })
            : level.text,
      }));
    },
    subjectOptions() {
      if (this.subjectLevel === null || this.subjectLevel === undefined) {
        return [];
      }

      return this.subjectsByLevel[this.subjectLevel] || [];
    },
    // Nome do assunto da aula, para o "Definir homework" (card 17trt6jen8u).
    subjectTitle() {
      const id = this.selectedLesson?.courseClassId;
      if (!id) return "";
      const subject = Object.values(this.subjectsByLevel)
        .flat()
        .find((item) => item && item.id === id);
      return subject?.title || "";
    },
    // Assunto de um nível diferente do nível da turma: a presença continua
    // valendo, isto é só um aviso para o professor conferir a escolha.
    lessonLevelMismatch() {
      if (!this.selectedLesson || !this.lessonSubjectId) {
        return false;
      }

      const level = this.subjectLevelById[this.lessonSubjectId];

      if (level === undefined || level === null) {
        return false;
      }

      return +level !== +this.groupLevel;
    },
    isDirty() {
      if (!this.selectedLesson) {
        return false;
      }

      const snapshot = this.snapshotsByLesson[this.selectedLesson.id] || {};

      return this.rosterMembers.some(
        (member) => this.statusOf(member.id) !== (snapshot[member.id] ?? null)
      );
    },
  },

  watch: {
    selectedLessonId: {
      immediate: true,
      handler() {
        // Default vem do proprio evento, como o cliente pediu: a aula ja traz o
        // conteudo previsto e o professor so mexe se tiver dado outra coisa.
        this.lessonSubjectId = this.selectedLesson
          ? this.selectedLesson.courseClassId || null
          : null;

        const known = this.lessonSubjectId
          ? this.subjectLevelById[this.lessonSubjectId]
          : undefined;

        this.subjectLevel =
          known !== undefined && known !== null ? known : this.groupLevel;

        this.ensureSubjects(this.subjectLevel);
      },
    },
  },

  async created() {
    await Promise.all([this.load(), this.ensureSubjects(this.groupLevel)]);
  },

  methods: {
    // Carrega sob demanda o nivel pedido. A lista completa de 15 niveis de uma
    // vez seria payload demais para uma tela em que o professor usa um nivel
    // por aula.
    async ensureSubjects(level) {
      if (level === null || level === undefined) {
        return;
      }

      if (this.subjectsByLevel[level]) {
        return;
      }

      this.loadingSubjects = true;

      try {
        const subjects = await this.fetchSubjects(level);

        this.$set(this.subjectsByLevel, level, subjects);

        subjects.forEach((subject) => {
          this.$set(this.subjectLevelById, subject.id, level);
        });
      } finally {
        this.loadingSubjects = false;
      }
    },
    async changeSubjectLevel(level) {
      await this.ensureSubjects(level);
    },
    async fetchSubjects(level) {
      try {
        const res = await partnerApi.getClassContentByLevel(level);
        const items = Array.isArray(res.data)
          ? res.data
          : res.data?.content || res.data?.data || [];

        // `level` acompanha o assunto para a tela poder avisar quando o tema
        // escolhido é de um nível diferente do da turma.
        // A API devolve as aulas na sequência do módulo; `position` é esse
        // número, mostrado na frente do título (card 86ajw35m1).
        return (items || []).map((item, index) => ({
          id: item.id,
          title: item.title,
          position: lessonPosition(index),
          level,
          category: item.category ?? null,
          pre: !!item.pre,
          optional: !!item.optional,
        }));
      } catch (error) {
        return [];
      }
    },
    async changeSubject(courseClassId) {
      if (
        !this.selectedLesson ||
        !courseClassId ||
        courseClassId === this.selectedLesson.courseClassId
      ) {
        return;
      }

      this.savingSubject = true;

      try {
        const res = await partnerApi.updateLessonSubject(
          this.groupId,
          this.selectedLesson.id,
          courseClassId
        );

        const index = this.lessons.findIndex(
          (item) => item.id === this.selectedLesson.id
        );

        if (index !== -1) {
          this.$set(this.lessons, index, {
            ...this.lessons[index],
            courseClassId: res.data?.courseClassId ?? courseClassId,
            title: res.data?.title ?? this.lessons[index].title,
          });
        }

        this.$root.$emit("display-alert", {
          text: this.$t("attendanceTab.subjectUpdated"),
          type: 0,
        });
      } catch (error) {
        // reverte a seleção visual para o valor atual da aula
        this.lessonSubjectId = this.selectedLesson.courseClassId || null;
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("attendanceTab.subjectUpdateFailed"),
          type: 1,
        });
      } finally {
        this.savingSubject = false;
      }
    },
    async load(monthKey) {
      if (!this.members.length) {
        return;
      }

      this.loading = true;

      try {
        const period = this.parseMonthKey(monthKey || this.selectedMonthKey);
        const res = await partnerApi.getGroupLessonRoster(this.groupId, {
          month: period?.month,
          year: period?.year,
        });

        this.applyRoster(res.data);
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: this.$t("attendanceTab.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    applyRoster(data) {
      this.availableMonths = Array.isArray(data?.availableMonths)
        ? data.availableMonths
        : [];
      this.selectedMonthKey = this.buildMonthKey(data?.month, data?.year);
      this.lessons = Array.isArray(data?.lessons) ? data.lessons : [];
      this.draftsByLesson = {};
      this.snapshotsByLesson = {};

      this.lessons.forEach((lesson) => this.hydrateLesson(lesson));

      const defaultLesson = this.pickDefaultLesson();
      this.selectedLessonId = defaultLesson ? defaultLesson.id : null;
    },
    hydrateLesson(lesson) {
      const snapshot = {};

      (lesson.attendances || []).forEach((entry) => {
        snapshot[entry.customerId] =
          entry.status === undefined || entry.status === null
            ? null
            : +entry.status;
      });

      this.$set(this.snapshotsByLesson, lesson.id, snapshot);
      this.$set(this.draftsByLesson, lesson.id, { ...snapshot });
    },
    pickDefaultLesson() {
      const pastLessons = this.lessons.filter((lesson) => !lesson.isFuture);

      if (pastLessons.length) {
        return pastLessons[pastLessons.length - 1];
      }

      return this.lessons[0] || null;
    },
    isLinkedToEvent(customerId) {
      const entry = (this.selectedLesson?.roster || []).find(
        (item) => item.customerId === customerId
      );

      // Sem roster (resposta antiga da API) não há o que sinalizar.
      return entry ? entry.linkedToEvent !== false : true;
    },
    statusOf(customerId) {
      if (!this.selectedLesson) {
        return null;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      const status = draft[customerId];

      return status === undefined || status === null ? null : +status;
    },
    savedStatusOf(customerId) {
      if (!this.selectedLesson) {
        return null;
      }

      const snapshot = this.snapshotsByLesson[this.selectedLesson.id] || {};
      const status = snapshot[customerId];

      return status === undefined || status === null ? null : +status;
    },
    setStatus(customerId, status) {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      this.$set(draft, customerId, status);
      this.$set(this.draftsByLesson, this.selectedLesson.id, draft);
    },
    markAll(status) {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      this.rosterMembers.forEach((member) => this.setStatus(member.id, status));
    },
    async handleMonthChange() {
      await this.load(this.selectedMonthKey);
    },
    handleLessonChange() {
      // draft/snapshot already hydrated per lesson; nothing else to do
    },
    async save() {
      if (!this.selectedLesson || this.selectedLesson.isFuture) {
        return;
      }

      const draft = this.draftsByLesson[this.selectedLesson.id] || {};
      const attendances = this.rosterMembers
        .map((member) => ({
          customerId: member.id,
          status: draft[member.id],
        }))
        .filter((entry) => entry.status !== undefined && entry.status !== null);

      if (!attendances.length) {
        this.$root.$emit("display-alert", {
          text: this.$t("attendanceTab.markOne"),
          type: 1,
        });
        return;
      }

      this.saving = true;

      try {
        const res = await partnerApi.saveLessonAttendance(
          this.groupId,
          this.selectedLesson.id,
          { attendances }
        );

        this.applyLessonUpdate(res.data);

        this.$root.$emit("display-alert", {
          text: this.$t("attendanceTab.saved"),
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("attendanceTab.saveFailed"),
          type: 1,
        });
      } finally {
        this.saving = false;
      }
    },
    applyLessonUpdate(lesson) {
      if (!lesson?.id) {
        return;
      }

      const index = this.lessons.findIndex((item) => item.id === lesson.id);

      if (index !== -1) {
        this.$set(this.lessons, index, lesson);
      }

      this.hydrateLesson(lesson);
    },
    buildMonthKey(month, year) {
      if (!month || !year) {
        return null;
      }

      return `${year}-${String(month).padStart(2, "0")}`;
    },
    parseMonthKey(monthKey) {
      if (!monthKey || !String(monthKey).includes("-")) {
        return null;
      }

      const [year, month] = String(monthKey).split("-").map(Number);

      if (!year || !month) {
        return null;
      }

      return { month, year };
    },
    initials(name) {
      return initialsFromName(name);
    },
    formatLessonDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat(languageTag(), {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      })
        .format(date)
        .replace(".", "");
    },
  },
};
</script>

<style scoped>
.attendance-tab__intro {
  margin-bottom: 4px;
}

.attendance-tab__hint {
  color: var(--dk-muted, #6b6472);
  font-size: 0.92rem;
  margin-top: 4px;
}

.attendance-tab__lesson-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
}

.attendance-tab__lesson-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.attendance-tab__lesson-date {
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6472);
  text-transform: capitalize;
}

.attendance-tab__quick {
  display: flex;
  gap: 4px;
}

.attendance-tab__roster {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attendance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  background: var(--dk-surface, #ffffff);
}

.attendance-row__name {
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.attendance-row__subject {
  display: flex;
  align-items: center;
  margin-top: 2px;
  font-size: 0.76rem;
  color: var(--dk-faint, #94a3b8);
}

.attendance-row__subject--done {
  color: var(--dk-teal, #00a379);
}

.attendance-row__initials {
  font-size: 0.8rem;
  font-weight: 700;
}

.attendance-row__toggle {
  display: flex;
  align-items: center;
}

.attendance-row__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}

.attendance-tab__course-hint {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--dk-teal-deep, #007f5e);
}

.attendance-tab__unsaved {
  color: var(--dk-muted, #6b6472);
}

.attendance-subject {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.attendance-subject__index {
  flex: none;
  min-width: 20px;
  color: var(--dk-faint, #9a96a6);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.attendance-subject__title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .attendance-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .attendance-row__toggle {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
